// Fetch full ticker details: price, yield, and asset class from Yahoo Finance
// Uses v8 chart for price and v10 quoteSummary for yield + category
import { fetchQuotes } from './fetchQuotes';

const ASSET_CLASS_OPTIONS = [
  'US Equities', 'International Equities', 'Fixed Income', 'Real Estate', 'Cash', 'Crypto', 'Other',
];

// Map Yahoo Finance fund category to our asset class options
function mapCategoryToAssetClass(category, quoteType, exchangeName) {
  if (!category && !quoteType) return null;

  const cat = (category || '').toLowerCase();

  // Bond / fixed income funds
  if (cat.includes('bond') || cat.includes('fixed income') || cat.includes('money market')
    || cat.includes('treasury') || cat.includes('inflation-protected')) {
    return 'Fixed Income';
  }

  // Real estate
  if (cat.includes('real estate') || cat.includes('reit')) {
    return 'Real Estate';
  }

  // International / foreign / emerging / global / world
  if (cat.includes('foreign') || cat.includes('international') || cat.includes('emerging')
    || cat.includes('world') || cat.includes('global') || cat.includes('europe')
    || cat.includes('asia') || cat.includes('china') || cat.includes('japan')
    || cat.includes('pacific') || cat.includes('latin')) {
    return 'International Equities';
  }

  // Domestic equity (blend, growth, value, stock, equity, large, mid, small)
  if (cat.includes('blend') || cat.includes('growth') || cat.includes('value')
    || cat.includes('equity') || cat.includes('stock') || cat.includes('large')
    || cat.includes('mid') || cat.includes('small') || cat.includes('dividend')
    || cat.includes('technology') || cat.includes('health') || cat.includes('energy')
    || cat.includes('utilities') || cat.includes('communications') || cat.includes('consumer')
    || cat.includes('financial') || cat.includes('industrials')) {
    return 'US Equities';
  }

  // Crypto
  if (quoteType === 'CRYPTOCURRENCY') return 'Crypto';

  // Individual stocks — default to US Equities for US exchanges
  if (quoteType === 'EQUITY') {
    const exch = (exchangeName || '').toUpperCase();
    if (exch.includes('NYSE') || exch.includes('NAS') || exch.includes('NYQ')
      || exch.includes('NMS') || exch.includes('PCX') || exch.includes('BTS')
      || exch.includes('NGM') || exch === 'AMEX') {
      return 'US Equities';
    }
    return 'International Equities';
  }

  return null;
}

// Extract a raw numeric value from Yahoo Finance's response format
// Yahoo sometimes returns { raw: 0.015 } and sometimes just the number
function rawVal(v) {
  if (v == null) return null;
  if (typeof v === 'number') return v;
  if (typeof v === 'object' && v.raw != null) return v.raw;
  return null;
}

/**
 * Fetch full details for a single ticker:
 *   { currentPrice, yieldRate (%), assetClass }
 * Returns null if ticker not found.
 */
export async function fetchTickerInfo(ticker) {
  const upper = ticker.trim().toUpperCase();
  if (!upper) return null;

  // Fire both requests in parallel
  const [priceResult, summaryResult] = await Promise.allSettled([
    fetchQuotes([upper]),
    fetch(`/api/yf-quote-summary/${encodeURIComponent(upper)}?modules=summaryDetail,quoteType,fundProfile`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null),
  ]);

  // Extract price
  const priceData = priceResult.status === 'fulfilled' ? priceResult.value : {};
  const currentPrice = priceData[upper]?.currentPrice ?? null;

  // Extract yield and asset class from quoteSummary
  let yieldRate = null;
  let assetClass = null;

  const summary = summaryResult.status === 'fulfilled' ? summaryResult.value : null;
  const result = summary?.quoteSummary?.result?.[0];
  if (result) {
    const sd = result.summaryDetail || {};
    const qt = result.quoteType || {};
    const fp = result.fundProfile || {};

    // Yield: prefer trailingAnnualDividendYield, fallback to yield (for bond funds)
    const divYield = rawVal(sd.trailingAnnualDividendYield) ?? rawVal(sd.yield);
    if (divYield != null && divYield > 0) {
      // Yahoo returns as decimal (0.0138 = 1.38%), convert to percentage
      yieldRate = Math.round(divYield * 10000) / 100; // e.g., 0.0138 → 1.38
    }

    // Asset class: map from fund category or quote type
    const category = fp.categoryName || '';
    const quoteType = qt.quoteType || '';
    const exchange = qt.exchange || '';
    assetClass = mapCategoryToAssetClass(category, quoteType, exchange);
  }

  return {
    currentPrice,
    yieldRate,
    assetClass: assetClass || ASSET_CLASS_OPTIONS[0], // default to 'US Equities'
  };
}
